import { useState, useCallback, useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import type { IrlEvent } from 'src/gql/graphql';
import useUser from 'hooks/use-user';
import { useSearchEnterprise } from 'src/enterprise/hooks/use-search-enterprise';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';
import { useIrlEvent } from './use-irl-event';
import { useIrlEventParams } from './use-irl-event-params';
import { useLoyaltyStatus } from './use-loyalty-status';
import { AuthOptions, useIrlEventAuth } from './use-irl-event-auth';
import { getInitialStep, getNextStep } from '../utils/step-navigation';
import { FLOW_STEPS, FlowStep, LoyaltyUserType } from '../types';

export type UseIrlEventFlowReturn = {
  currentStep: FlowStep;
  goBack: () => void;
  goToNext: (target?: FlowStep) => void;
  handleAuthSuccess: (options: AuthOptions) => Promise<void>;
  hasLoyaltyProgram: boolean;
  irlEvent: IrlEvent | null;
  isEligibleForReward: boolean;
  isEventClosed: boolean;
  isLoading: boolean;
  isLoyaltyMember: boolean;
  loyaltyUserType: LoyaltyUserType;
  optInRequired: boolean;
  setDidOptInToLoyalty: (value: boolean) => void;
  shopNowUrl: string | null;
};

export function useIrlEventFlow(): UseIrlEventFlowReturn {
  const [stepHistory, setStepHistory] = useState<FlowStep[]>([]);
  const [didOptInToLoyalty, setDidOptInToLoyalty] = useState(false);

  const { enterpriseId, isoRegionCode, dispensaryId, isRouterReady } = useIrlEventParams();
  const isDispensaryPath = !!dispensaryId;

  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn) ?? false;
  const userLoading = useObserver(() => user.loading) ?? true;
  const userPhone = useObserver(() => user.phone) ?? '';
  const isUserReady = !userLoading && isLoggedIn && !!userPhone;
  const needsProfileCompletion = !userLoading && isLoggedIn && !userPhone;

  const { enterprise: enterpriseLoyaltySettings, loading: enterpriseLoading } = useSearchEnterprise({ enterpriseId });
  const { irlEvent, loading: irlEventLoading } = useIrlEvent();
  const optInRequired = irlEvent?.optInRequired ?? false;

  // Loyalty config drives shouldAutoEnroll for silent DPOS creation
  const { data: loyaltyConfig, loading: loyaltyConfigLoading } = useGetDutchiePosLoyaltyConfig(
    isDispensaryPath ? { dispensaryId } : { enterpriseId, isoRegionCode }
  );

  const shouldAutoEnroll = useMemo(() => {
    const loyaltyRequireOptIn = loyaltyConfig?.loyaltyRequireOptIn ?? true;
    return !loyaltyRequireOptIn && !optInRequired;
  }, [loyaltyConfig?.loyaltyRequireOptIn, optInRequired]);

  // Loyalty enrollment check
  const {
    hasLoyaltyProgram,
    showLoyaltyEnrollment,
    isLoyaltyMember,
    isLoading: isLoyaltyStatusLoading,
  } = useLoyaltyStatus({
    dispensaryId,
    enterpriseLoyaltySettings,
    isoRegionCode,
    isUserReady,
    optInRequired,
    userPhone,
  });

  const isEligibleForReward = !optInRequired || didOptInToLoyalty || isLoyaltyMember;
  const isEventClosed = irlEvent?.isOpen === false;
  const shopNowUrl = irlEvent?.shopNowUrl ?? enterpriseLoyaltySettings?.landingPageUrl ?? null;

  // Determine loyalty user type for tracking
  const loyaltyUserType: LoyaltyUserType = (() => {
    if (didOptInToLoyalty) {
      return 'new';
    }
    if (isLoyaltyMember) {
      return 'existing';
    }
    return 'none';
  })();

  // Current step: use history if navigated, otherwise compute initial step
  const currentStep =
    stepHistory.length > 0
      ? stepHistory[stepHistory.length - 1]
      : getInitialStep({ isLoggedIn, showLoyaltyEnrollment });

  // Reset step history and loyalty opt-in state when user logs out from a post-auth screen
  const isPostAuthStep = currentStep === FLOW_STEPS.EVENT_LOYALTY_ENROLL || currentStep === FLOW_STEPS.EVENT_SIGNUP_END;
  useEffect(() => {
    if (!isLoggedIn && isPostAuthStep) {
      setStepHistory([]);
      setDidOptInToLoyalty(false);
    }
  }, [isLoggedIn, isPostAuthStep]);

  // Navigate forward - either to an explicit target or computed next step
  const goToNext = useCallback(
    (target?: FlowStep): void => {
      const nextStep =
        target ??
        getNextStep({
          currentStep,
          showLoyaltyEnrollment,
        });

      if (nextStep) {
        setStepHistory((prev) => {
          // If history is empty, initialize with current step before adding next
          if (prev.length === 0) {
            return [currentStep, nextStep];
          }
          return [...prev, nextStep];
        });
      }
    },
    [currentStep, showLoyaltyEnrollment]
  );

  // Navigate backward - pop from history stack
  const goBack = useCallback((): void => {
    setStepHistory((prev) => {
      if (prev.length <= 1) {
        return [];
      }
      return prev.slice(0, -1);
    });
  }, []);

  // User authentication processing
  const { handleAuthSuccess, isProcessingAuth } = useIrlEventAuth({
    dispensaryId,
    enterpriseId,
    isoRegionCode,
    userPhone,
    isUserReady,
    needsProfileCompletion,
    loyaltyConfigLoading,
    loyaltyStatusLoading: isLoyaltyStatusLoading,
    isLoyaltyMember,
    shouldAutoEnroll,
    onAutoEnroll: () => setDidOptInToLoyalty(true),
    onAuthComplete: () => setStepHistory([]),
  });

  const isLoading =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR: any loading state should yield true
    !isRouterReady || irlEventLoading || enterpriseLoading || userLoading || isLoyaltyStatusLoading || isProcessingAuth;

  return {
    currentStep,
    shopNowUrl,
    goBack,
    goToNext,
    handleAuthSuccess,
    hasLoyaltyProgram,
    irlEvent,
    isEligibleForReward,
    isEventClosed,
    isLoading,
    isLoyaltyMember,
    loyaltyUserType,
    optInRequired,
    setDidOptInToLoyalty,
  };
}
